import apiClient, { tokenManager } from './apiClient';

const authService = {
  /**
   * Login with username/email and password
   * Returns user data and stores JWT tokens
   */
  login: async (credentials) => {
    const response = await apiClient.post('/auth/login', credentials);
    const { access_token, refresh_token, user } = response.data;
    
    // Store tokens
    tokenManager.setTokens(access_token, refresh_token);
    
    // Store user data
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }
    
    return response.data;
  },

  /**
   * Register new user account
   */
  register: async (userData) => {
    const response = await apiClient.post('/auth/register', userData);
    const { access_token, refresh_token, user } = response.data;
    
    // Store tokens
    tokenManager.setTokens(access_token, refresh_token);
    
    // Store user data
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }
    
    return response.data;
  },

  /**
   * Logout current user and clear tokens
   */
  logout: async () => {
    try {
      // Call logout endpoint to blacklist current token
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local storage
      tokenManager.clearTokens();
    }
  },

  /**
   * Logout from all devices (invalidates all tokens)
   */
  logoutAll: async () => {
    try {
      await apiClient.post('/auth/logout-all');
    } catch (error) {
      console.error('Logout all error:', error);
    } finally {
      tokenManager.clearTokens();
    }
  },

  /**
   * Get current user from API (verifies token is valid)
   */
  getCurrentUser: async () => {
    try {
      const response = await apiClient.get('/auth/me');
      const userData = response.data.user;
      
      if (userData) {
        localStorage.setItem('user', JSON.stringify(userData));
        return userData;
      }
    } catch (error) {
      // If token is invalid, clear everything
      tokenManager.clearTokens();
      return null;
    }
  },

  /**
   * Get current user from localStorage (cached)
   */
  getCachedUser: () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (error) {
        console.error('Failed to parse user data:', error);
        return null;
      }
    }
    return null;
  },

  /**
   * Check if user is authenticated (has valid token)
   */
  isAuthenticated: () => {
    return !!tokenManager.getAccessToken();
  },

  /**
   * Refresh access token using refresh token
   */
  refreshToken: async () => {
    const refreshToken = tokenManager.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await apiClient.post('/auth/refresh', {}, {
      headers: {
        Authorization: `Bearer ${refreshToken}`
      }
    });

    const { access_token } = response.data;
    tokenManager.setAccessToken(access_token);
    
    return access_token;
  },

  /**
   * Verify if current token is valid
   */
  verifyToken: async () => {
    try {
      const response = await apiClient.get('/auth/verify-token');
      return response.data.valid;
    } catch (error) {
      return false;
    }
  }
};

export default authService;
